import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button/Button';

const CartWrapper = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing.xxl} 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.background.secondary} 0%, ${props => props.theme.colors.background.primary} 100%);
  // padding-top: 120px;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const CartHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxxl};
  position: relative;
`;

const CartTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CartSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const ProgressIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  &::after {
    content: '';
    width: 40px;
    height: 2px;
    background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.border.light};
    margin-left: ${props => props.theme.spacing.lg};
  }

  &:last-child::after {
    display: none;
  }
`;

const StepIndicator = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.background.secondary};
  color: ${props => props.active ? 'white' : props.theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.bold};
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border.light};
`;

const StepLabel = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const CartContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${props => props.theme.spacing.xxxl};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 350px;
    gap: ${props => props.theme.spacing.xxl};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const CartItemsSection = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border.light};
`;

const CartItemsHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  background: linear-gradient(135deg, ${props => props.theme.colors.background.secondary}, white);
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const CartItemsTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const CartItemsList = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: ${props => props.theme.spacing.xl};
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border.light};
  background: ${props => props.theme.colors.background.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
  transition: all ${props => props.theme.transitions.medium};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.primary}40;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 80px 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const ItemImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const ItemImage = styled.div`
  width: 100px;
  height: 100px;
  background-color: ${props => props.theme.colors.background.secondary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: transform ${props => props.theme.transitions.medium};

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 80px;
    height: 80px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  flex: 1;
`;

const ItemName = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.3;
  margin: 0;
`;

const ItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const ItemPrice = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const ItemSKU = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: monospace;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  align-items: flex-end;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: ${props => props.theme.spacing.md};
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const QuantityButton = styled.button`
  background: ${props => props.theme.colors.background.secondary};
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  transition: all ${props => props.theme.transitions.fast};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.div`
  min-width: 50px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  background: white;
`;

const ItemTotal = styled.div`
  text-align: right;
`;

const ItemTotalLabel = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ItemTotalPrice = styled.div`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

const RemoveButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.error}40;
  color: ${props => props.theme.colors.error};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background-color: ${props => props.theme.colors.error};
    color: white;
    transform: translateY(-1px);
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.theme.colors.border.light};
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: ${props => props.theme.borderRadius.xl} ${props => props.theme.borderRadius.xl} 0 0;
`;

const SummaryTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: white;
  margin: 0;
`;

const SummaryContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.md} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};

  &:last-of-type {
    border-bottom: none;
    margin-top: ${props => props.theme.spacing.lg};
    padding-top: ${props => props.theme.spacing.lg};
    border-top: 2px solid ${props => props.theme.colors.border.light};
  }
`;

const SummaryLabel = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const SummaryValue = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.semibold};
`;

const TotalRow = styled(SummaryRow)`
  background: linear-gradient(135deg, ${props => props.theme.colors.background.secondary}, transparent);
  margin: ${props => props.theme.spacing.lg} -${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const TotalLabel = styled(SummaryLabel)`
  font-size: ${props => props.theme.fontSize.lg};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const TotalValue = styled(SummaryValue)`
  font-size: ${props => props.theme.fontSize.xxl};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const PromoSection = styled.div`
  margin: ${props => props.theme.spacing.lg} 0;
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px dashed ${props => props.theme.colors.primary}40;
`;

const PromoInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  margin-bottom: ${props => props.theme.spacing.sm};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ApplyPromoButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.primary}20;
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const CheckoutActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.success}10;
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const SecurityText = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.success};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxxl};
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.theme.colors.border.light};
`;

const EmptyCartIcon = styled.div`
  width: 120px;
  height: 120px;
  margin: 0 auto ${props => props.theme.spacing.xl};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.primaryDark}20);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  color: ${props => props.theme.colors.primary};
`;

const EmptyCartTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const EmptyCartText = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xxl};
  line-height: 1.6;
`;

const CartPage = () => {
  const { items, total, itemCount, updateQuantity, removeItem, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [promoCode, setPromoCode] = useState('');

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId);
    } else {
      updateQuantity(itemId, newQuantity);
    }
  };

  const formatPrice = (price) => `₹${parseFloat(price).toFixed(2)}`;

  const handleApplyPromo = () => {
    // Implement promo code logic here
    console.log('Applying promo code:', promoCode);
  };

  if (items.length === 0) {
    return (
      <CartWrapper>
        <Container>
          <CartHeader>
            <CartTitle>Shopping Cart</CartTitle>
            <CartSubtitle>Your journey to amazing products starts here</CartSubtitle>
          </CartHeader>
          
          <EmptyCart>
            <EmptyCartIcon>🛒</EmptyCartIcon>
            <EmptyCartTitle>Your cart is empty</EmptyCartTitle>
            <EmptyCartText>
              Discover our amazing collection of products and find something you'll love.
              <br />
              Start shopping and add items to your cart to see them here.
            </EmptyCartText>
            <Button as={Link} to="/shop" size="lg">
              Start Shopping
            </Button>
          </EmptyCart>
        </Container>
      </CartWrapper>
    );
  }

  return (
    <CartWrapper>
      <Container>
        <CartHeader>
          <CartTitle>Shopping Cart</CartTitle>
          <CartSubtitle>Review your items and proceed to checkout</CartSubtitle>
          
          <ProgressIndicator>
            <ProgressStep active>
              <StepIndicator active>1</StepIndicator>
              <StepLabel active>Cart</StepLabel>
            </ProgressStep>
            <ProgressStep>
              <StepIndicator>2</StepIndicator>
              <StepLabel>Checkout</StepLabel>
            </ProgressStep>
            <ProgressStep>
              <StepIndicator>3</StepIndicator>
              <StepLabel>Payment</StepLabel>
            </ProgressStep>
            <ProgressStep>
              <StepIndicator>4</StepIndicator>
              <StepLabel>Complete</StepLabel>
            </ProgressStep>
          </ProgressIndicator>
        </CartHeader>

        <CartContent>
          <CartItemsSection>
            <CartItemsHeader>
              <CartItemsTitle>Your Items ({itemCount})</CartItemsTitle>
            </CartItemsHeader>
            
            <CartItemsList>
              {items.map((item) => (
                <CartItem key={item.id}>
                  <ItemImageContainer>
                    <ItemImage image={item.image} />
                  </ItemImageContainer>
                  
                  <ItemDetails>
                    <ItemName>{item.name}</ItemName>
                    <ItemMeta>
                      <ItemPrice>{formatPrice(item.price)} each</ItemPrice>
                      <ItemSKU>SKU: {item.id}</ItemSKU>
                    </ItemMeta>
                  </ItemDetails>

                  <ItemActions>
                    <QuantityControl>
                      <QuantityButton
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        −
                      </QuantityButton>
                      <QuantityDisplay>{item.quantity}</QuantityDisplay>
                      <QuantityButton
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      >
                        +
                      </QuantityButton>
                    </QuantityControl>

                    <ItemTotal>
                      <ItemTotalLabel>Total</ItemTotalLabel>
                      <ItemTotalPrice>
                        {formatPrice(item.price * item.quantity)}
                      </ItemTotalPrice>
                    </ItemTotal>

                    <RemoveButton onClick={() => removeItem(item.id)}>
                      Remove
                    </RemoveButton>
                  </ItemActions>
                </CartItem>
              ))}
            </CartItemsList>
          </CartItemsSection>

          <CartSummary>
            <SummaryHeader>
              <SummaryTitle>Order Summary</SummaryTitle>
            </SummaryHeader>
            
            <SummaryContent>
              <SummaryRow>
                <SummaryLabel>Subtotal ({itemCount} items)</SummaryLabel>
                <SummaryValue>{formatPrice(total)}</SummaryValue>
              </SummaryRow>

              <SummaryRow>
                <SummaryLabel>Shipping</SummaryLabel>
                <SummaryValue>Free</SummaryValue>
              </SummaryRow>

              <SummaryRow>
                <SummaryLabel>Tax</SummaryLabel>
                <SummaryValue>Calculated at checkout</SummaryValue>
              </SummaryRow>
{/* 
              <PromoSection>
                <PromoInput
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
                <ApplyPromoButton onClick={handleApplyPromo}>
                  Apply Code
                </ApplyPromoButton>
              </PromoSection> */}

              <TotalRow>
                <TotalLabel>Total</TotalLabel>
                <TotalValue>{formatPrice(total)}</TotalValue>
              </TotalRow>

               <CheckoutActions>
                {/* Always allow checkout - guest or authenticated */}
                <Button as={Link} to="/checkout" size="lg" fullWidth>
                  Proceed to Checkout
                </Button>
                
                <Button 
                  as={Link} 
                  to="/shop" 
                  variant="outline" 
                  size="lg" 
                  fullWidth
                >
                  Continue Shopping
                </Button>

                {/* Show sign in option but don't require it */}
                {!isAuthenticated && (
                  <Button 
                    as={Link} 
                    to="/signin" 
                    variant="secondary" 
                    size="sm"
                    style={{ marginTop: '0.5rem' }}
                  >
                    Sign In for Faster Checkout
                  </Button>
                )}

                <Button 
                  onClick={clearCart}
                  variant="danger" 
                  size="sm"
                  style={{ marginTop: '1rem' }}
                >
                  Clear Cart
                </Button>
              </CheckoutActions>

              <SecurityBadge>
                <span>🔒</span>
                <SecurityText>Secure checkout with SSL encryption</SecurityText>
              </SecurityBadge>
            </SummaryContent>
          </CartSummary>
        </CartContent>
      </Container>
    </CartWrapper>
  );
};

export default CartPage;